import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, Zap, Crown } from 'lucide-react';

function PricingTiers() {
  const tiers = [
    { 
      name: "Starter", 
      price: "$99", 
      period: "per month",
      description: "Perfect for small businesses and individual PR professionals",
      features: [
        "5 press releases per month",
        "Up to 100 media outlets",
        "Basic analytics dashboard",
        "Email distribution",
        "Standard templates",
        "Email support"
      ], 
      cta: "Start with Starter",
      icon: Zap,
      popular: false,
      color: "from-blue-500 to-blue-600"
    },
    { 
      name: "Professional", 
      price: "$299", 
      period: "per month",
      description: "Ideal for growing agencies and marketing teams",
      features: [
        "25 press releases per month",
        "Up to 500 media outlets",
        "Advanced analytics & reporting",
        "AI content optimization",
        "Custom templates",
        "Priority support",
        "Campaign scheduling",
        "Outlet targeting filters"
      ], 
      cta: "Go Professional",
      icon: Star,
      popular: true,
      color: "from-purple-500 to-purple-600"
    },
    { 
      name: "Enterprise", 
      price: "$699", 
      period: "per month",
      description: "Comprehensive solution for large organizations",
      features: [
        "Unlimited press releases",
        "1000+ premium outlets",
        "White-label solution",
        "Advanced AI content generation",
        "Custom integrations",
        "Dedicated account manager",
        "Real-time collaboration",
        "API access",
        "Custom reporting"
      ], 
      cta: "Contact Sales",
      icon: Crown,
      popular: false,
      color: "from-amber-500 to-amber-600"
    },
  ];

  const addOns = [
    { name: "AI Press Release Writing", price: "$150", description: "Professional PR content generated by AI" },
    { name: "Custom Press Kit Design", price: "$250", description: "Branded press kit with visual assets" },
    { name: "Media Monitoring", price: "$99", description: "Track mentions and coverage across web" },
    { name: "Influencer Outreach", price: "$199", description: "Connect with industry influencers" },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-4">Choose Your PR Distribution Plan</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Scale your PR efforts with our flexible pricing tiers. From startups to enterprises, 
          we have the perfect solution for your press release distribution needs.
        </p>
      </div>
      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {tiers?.map((tier, index) => {
          const IconComponent = tier?.icon;
          return (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-3xl border p-8 bg-white shadow-sm hover:shadow-md transition ${
                tier?.popular ? "ring-2 ring-purple-500 border-purple-200" : "border-gray-200"
              }`}
            >
              {tier?.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-r ${tier?.color} text-white mb-4`}>
                  <IconComponent size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">{tier?.name}</h3>
                <div className="mb-3">
                  <span className="text-4xl font-bold">{tier?.price}</span>
                  <span className="text-gray-500 ml-1">{tier?.period}</span>
                </div>
                <p className="text-sm text-gray-600">{tier?.description}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {tier?.features?.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0"/>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <button className={`w-full py-3 rounded-2xl font-semibold transition ${
                tier?.popular 
                  ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700" :"bg-black text-white hover:bg-gray-800"
              }`}>
                {tier?.cta}
              </button>
            </motion.div>
          );
        })}
      </div>
      {/* Add-ons Section */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-8">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold mb-2">Boost Your PR with Add-ons</h3>
          <p className="text-gray-600">Enhance your campaigns with our professional services</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {addOns?.map((addon, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-4 border border-gray-200 hover:shadow-md transition"
            >
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 mb-1">{addon?.price}</div>
                <div className="font-semibold mb-2">{addon?.name}</div>
                <p className="text-xs text-gray-600">{addon?.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      {/* FAQ Section */}
      <div className="bg-white rounded-3xl p-8 border border-gray-100">
        <h3 className="text-xl font-bold mb-6 text-center">Frequently Asked Questions</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-2">Can I change plans anytime?</h4>
            <p className="text-sm text-gray-600">Yes, you can upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle.</p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">What payment methods do you accept?</h4>
            <p className="text-sm text-gray-600">We accept all major credit cards, PayPal, and wire transfers for enterprise accounts.</p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">Is there a free trial?</h4>
            <p className="text-sm text-gray-600">Yes! We offer a 14-day free trial with access to all Starter plan features.</p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">Do you offer custom enterprise pricing?</h4>
            <p className="text-sm text-gray-600">Absolutely. Contact our sales team for volume discounts and custom feature sets.</p>
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="text-center bg-gradient-to-r from-black to-gray-800 text-white rounded-3xl p-8">
        <h3 className="text-2xl font-bold mb-4">Ready to Transform Your PR Strategy?</h3>
        <p className="mb-6 opacity-90">Join thousands of PR professionals who trust PenGame for their distribution needs</p>
        <div className="flex items-center justify-center gap-4">
          <button className="px-6 py-3 bg-white text-black rounded-2xl font-semibold hover:bg-gray-100 transition">
            Start Free Trial
          </button>
          <button className="px-6 py-3 border border-white/20 rounded-2xl font-semibold hover:bg-white/10 transition">
            Schedule Demo
          </button>
        </div>
      </div>
    </div>
  );
}

export default PricingTiers;